import React, { useEffect, useState, useContext, useCallback } from 'react';
import {
  Button,
  Typography,
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
  Alert,
  Autocomplete,
  Radio,
  RadioGroup,
  Box,
  Card,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EtDatePicker from 'mui-ethiopian-datepicker';
import PropTypes from 'prop-types';

import { NAME_REGEX, PHONE_REGEX } from '../../../utils/constants';
import axiosInstance from '../../../api';
import SERVER_API_URL from '../../../config';
import {
  INTERNAL_SERVER_ERROR,
  NAME_EXCEPTION,
  PHONE_EXCEPTION,
  REQUIRED_FIELD,
} from '../../../utils/exception';
import { authContext } from '../../../routes';
import { ASSISTANT_ADMIN_ROLE, CHURCH_ADMIN_ROLE, DIOCESE_ADMIN_ROLE, DISTRICT_ADMIN_ROLE } from '../../../utils/roles';

export default function UserRegistrationForm({ setUserToBeUpdated, notification, handleNext }) {
  const [disabled, setDisabled] = useState(false);
  const { user } = useContext(authContext);

  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [firstNameClicked, setFirstNameClicked] = useState(false);
  const [formErrorOnFirstName, setFormErrorOnFirstName] = useState(false);
  const [helperTextOnFirstName, setHelperTextOnFirstName] = useState(null);

  const [lastNameClicked, setLastNameClicked] = useState(false);
  const [formErrorOnLastName, setFormErrorOnLastName] = useState(false);
  const [helperTextOnLastName, setHelperTextOnLastName] = useState(null);

  const [christianNameClicked, setChristianNameClicked] = useState(false);
  const [formErrorOnChristianName, setFormErrorOnChristianName] = useState(false);
  const [helperTextOnChristianName, setHelperTextOnChristianName] = useState(null);

  const [positionClicked, setPositionClicked] = useState(false);
  const [formErrorOnPosition, setFormErrorOnPosition] = useState(false);
  const [helperTextOnPosition, setHelperTextOnPosition] = useState(null);

  const [phoneClicked, setPhoneClicked] = useState(false);
  const [formErrorOnPhone, setFormErrorOnPhone] = useState(false);
  const [helperTextOnPhone, setHelperTextOnPhone] = useState(null);

  const [birthDate, setBirthDate] = useState();
  const [formErrorOnBirthDate, setFormErrorOnBirthDate] = useState(false);
  const [helperTextOnBirthDate, setHelperTextOnBirthDate] = useState(null);

  const [educationLevelClicked, setEducationLevelClicked] = useState(false);
  const [formErrorOnEducationLevel, setFormErrorOnEducationLevel] = useState(false);
  const [helperTextOnEducationLevel, setHelperTextOnEducationLevel] = useState(null);

  const [formErrorOnMarriageStatus, setFormErrorOnMarriageStatus] = useState(false);
  const [helperTextOnMarriageStatus, setHelperTextOnMarriageStatus] = useState(null);

  const [formErrorOnGender, setFormErrorOnGender] = useState(false);
  const [helperTextOnGender, setHelperTextOnGender] = useState(null);

  const [categories, setCategories] = useState(null);
  const [categoryNameClicked, setCategoryNameClicked] = useState(false);
  const [formErrorOnCategoryName, setFormErrorOnCategoryName] = useState(false);
  const [helperTextOnCategoryName, setHelperTextOnCategoryName] = useState(null);

  const [church, setChurch] = useState(null);
  const [churchNameClicked, setChurchNameClicked] = useState(false);
  const [formErrorOnChurchName, setFormErrorOnChurchName] = useState(false);
  const [helperTextOnChurchName, setHelperTextOnChurchName] = useState(null);

  const [roles, setRoles] = useState(null);
  const [roleNameClicked, setRoleNameClicked] = useState(false);
  const [formErrorOnRoleName, setFormErrorOnRoleName] = useState(false);
  const [helperTextOnRoleName, setHelperTextOnRoleName] = useState(null);

  const handleSubmitNewUser = async (event) => {
    event.preventDefault();
    setFormErrorSeverity('error');
    setDisabled(true);

    const {
      firstName,
      lastName,
      christianName,
      position,
      phone,
      gender,
      categoryName,
      roleName,
      churchName,
      educationLevel,
      marriageStatus
    } = event.target.elements;

    if (!birthDate) {
      setFormErrorOnBirthDate(true);
      setHelperTextOnBirthDate('የትውልድ ዘመን ያስፈልጋል።');
    } else if (new Date() <= new Date(birthDate)) {
      setFormErrorOnBirthDate(true);
      setHelperTextOnBirthDate('የትውልድ ዘመን ክዛሬ መብለጥ አይችልም።');
    } else {
      setFormErrorOnBirthDate(false);
      setHelperTextOnBirthDate(null);
    }

    if (gender.value.length === 0) {
      setFormErrorOnGender(true);
      setHelperTextOnGender(REQUIRED_FIELD);
    } else {
      setFormErrorOnGender(false);
      setHelperTextOnGender(null);
    }

    if (marriageStatus.value.length === 0) {
      setFormErrorOnMarriageStatus(true);
      setHelperTextOnMarriageStatus(REQUIRED_FIELD);
    } else {
      setFormErrorOnMarriageStatus(false);
      setHelperTextOnMarriageStatus(null);
    }

    if (
      !formErrorOnFirstName &&
      !formErrorOnLastName &&
      !formErrorOnChristianName &&
      !formErrorOnPosition &&
      !formErrorOnPhone &&
      !formErrorOnEducationLevel &&
      !formErrorOnBirthDate &&
      !formErrorOnMarriageStatus &&
      !formErrorOnGender &&
      !formErrorOnCategoryName &&
      !formErrorOnRoleName &&
      !formErrorOnChurchName
    ) {

      try {

        const response = await axiosInstance.post(`${SERVER_API_URL}/user/userRegister/${user && user.id}`, {
          firstName: firstName.value,
          lastName: lastName.value,
          christianName: christianName.value,
          position: position.value,
          phone: phone.value,
          educationLevel: educationLevel.value,
          marriageStatus: marriageStatus.value,
          gender: gender.value,
          categoryName: categoryName.value,
          roleName: roleName.value,
          churchName: churchName.value || user?.role.name,
          districtName: 'ሀገረ ስብከት',
          birthDate
        });

        if (response.status === 201) {
          notification.message = 'ዋና የተጠቃሚ መረጃ በተሳካ ሁኔታ ተቀምጧል';
          notification.severity = 'success';
          setUserToBeUpdated(response.data);
          handleNext();
        }
      } catch (e) {
        if (e.response.status === 406 || e.response.status === 400) {
          setFormError(e.response.data.message);
        } else if (e.response.status === 401) {
          setFormError("እርስዎ ይህን ቦታ እንዲሞሉ አልተፈቀሎትም።");
        } else if (e.response.status === 500) {
          setFormError(INTERNAL_SERVER_ERROR);
        }
      }
    } else {
      setFormError('እባክዎ አስፈላጊዎቹን መስኮች ይሙሉ!');
      if (gender.value.length === 0) {
        setFormErrorOnGender(true);
        setFormError('ጾታ ባዶ መሆን አይችልም።');
      } else {
        setFormErrorOnGender(false);
        setHelperTextOnGender(null);
      }

      if (marriageStatus.value.length === 0) {
        setFormErrorOnMarriageStatus(true);
        setFormError('የጋብቻ ሁኔታ ባዶ መሆን አይችልም');
      } else {
        setFormErrorOnMarriageStatus(false);
        setHelperTextOnMarriageStatus(null);
      }
    }
    setDisabled(false);
  };

  const getCategories = async () => {
    try {
      const response = await axiosInstance.get(`${SERVER_API_URL}/category/findAll`);
      if (response.status === 200) {
        setCategories(response.data);
      }
    } catch (error) {
      // console.log(error);
    }
  };
  const getRoles = useCallback(async () => {

    if (user.role.name === 'የአጥቢያ አስተዳዳሪ' || user.role.name === 'የሃገረስብከት አጥቢያ አስተዳዳሪ') {
      setRoles(CHURCH_ADMIN_ROLE);
    }

    if (user.role.name === 'የአውራጃ አስተዳዳሪ') {
      setRoles(DISTRICT_ADMIN_ROLE);
    }

    if (user.role.name === 'ረዳት አስተዳዳሪ') {
      setRoles(ASSISTANT_ADMIN_ROLE);
    }

    if (user.role.name === 'አጠቃላይ አስተዳዳሪ') {
      setRoles(DIOCESE_ADMIN_ROLE);
    }
  }, [user, setRoles]);

  useEffect(() => {
    const getChurch = async () => {
      try {
        const response = await axiosInstance.get(`${SERVER_API_URL}/church/findManyByDistrictName/ሀገረ ስብከት`);
        if (response.status === 200) {
          setChurch(response.data);
        }
      } catch (error) {
        // console.log(error);
      }
    };
    getChurch();
    getRoles();
    getCategories();
  }, [getRoles]);

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');

    const { name, value } = e.target;
    switch (name) {
      case 'firstName':
        if (value.length === 0) {
          setFirstNameClicked(false);
          setFormErrorOnFirstName(firstNameClicked && value.length === 0);
          setHelperTextOnFirstName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnFirstName(true);
          setHelperTextOnFirstName(NAME_EXCEPTION);
        } else {
          setHelperTextOnFirstName(null);
          setFormErrorOnFirstName(false);
        }
        break;
      case 'lastName':
        if (value.length === 0) {
          setLastNameClicked(false);
          setFormErrorOnLastName(lastNameClicked && value.length === 0);
          setHelperTextOnLastName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnLastName(true);
          setHelperTextOnLastName(NAME_EXCEPTION);
        } else {
          setHelperTextOnLastName(null);
          setFormErrorOnLastName(false);
        }
        break;
      case 'christianName':
        if (value.length === 0) {
          setChristianNameClicked(false);
          setFormErrorOnChristianName(christianNameClicked && value.length === 0);
          setHelperTextOnChristianName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnChristianName(true);
          setHelperTextOnChristianName(NAME_EXCEPTION);
        } else {
          setHelperTextOnChristianName(null);
          setFormErrorOnChristianName(false);
        }
        break;
      case 'position':
        if (value.length === 0) {
          setPositionClicked(false);
          setFormErrorOnPosition(positionClicked && value.length === 0);
          setHelperTextOnPosition(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnPosition(true);
          setHelperTextOnPosition(NAME_EXCEPTION);
        } else {
          setHelperTextOnPosition(null);
          setFormErrorOnPosition(false);
        }
        break;
      case 'phone':
        if (value.length === 0) {
          setPhoneClicked(false);
          setFormErrorOnPhone(phoneClicked && value.length === 0);
          setHelperTextOnPhone(REQUIRED_FIELD);
        } else if (!PHONE_REGEX.test(value)) {
          setFormErrorOnPhone(true);
          setHelperTextOnPhone(PHONE_EXCEPTION);
        } else {
          setHelperTextOnPhone(null);
          setFormErrorOnPhone(false);
        }
        break;
      case 'educationLevel':
        if (value.length === 0) {
          setEducationLevelClicked(false);
          setFormErrorOnEducationLevel(educationLevelClicked && value.length === 0);
          setHelperTextOnEducationLevel(REQUIRED_FIELD);
        } else {
          setHelperTextOnEducationLevel(null);
          setFormErrorOnEducationLevel(false);
        }
        break;
      case 'categoryName':
        if (value.length === 0) {
          setCategoryNameClicked(false);
          setFormErrorOnCategoryName(categoryNameClicked && value.length === 0);
          setHelperTextOnCategoryName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnCategoryName(true);
          setHelperTextOnCategoryName(NAME_EXCEPTION);
        } else if (!categories || !categories.some((obj) => obj.name === value)) {
          setFormErrorOnCategoryName(true);
          setHelperTextOnCategoryName('የምድብ ስም አስቀድሞ ከተመዘገቡ ምድቦች ውስጥ አንዱ መሆን አለበት።');
        } else {
          setHelperTextOnCategoryName(null);
          setFormErrorOnCategoryName(false);
        }
        break;
      case 'churchName':
        if (value.length === 0) {
          setChurchNameClicked(false);
          setFormErrorOnChurchName(churchNameClicked && value.length === 0);
          setHelperTextOnChurchName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnChurchName(true);
          setHelperTextOnChurchName(NAME_EXCEPTION);
        } else if (!church || !church.some((obj) => obj.name === value)) {
          setFormErrorOnChurchName(true);
          setHelperTextOnChurchName('የአጥቢያ ስም አስቀድሞ ከተመዘገበ አጥቢያ አንዱ መሆን አለበት።');
        } else {
          setHelperTextOnChurchName(null);
          setFormErrorOnChurchName(false);
        }
        break;
      case 'roleName':
        if (value.length === 0) {
          setRoleNameClicked(false);
          setFormErrorOnRoleName(roleNameClicked && value.length === 0);
          setHelperTextOnRoleName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnRoleName(true);
          setHelperTextOnRoleName(NAME_EXCEPTION);
        } else if (!DIOCESE_ADMIN_ROLE.includes(value)) {
          setFormErrorOnRoleName(true);
          setHelperTextOnRoleName('የሚና ስም አስቀድሞ ከተመዘገቡት ሚናዎች ውስጥ አንዱ መሆን አለበት።');
        } else {
          setHelperTextOnRoleName(null);
          setFormErrorOnRoleName(false);
        }
        break;
      default:
        break;
    }
  };

  const handleFormValueChange = (e) => {
    e.preventDefault();
    setFormError(null);
    setDisabled(false);
    setFormErrorSeverity('error');
    const { name } = e.target;
    switch (name) {
      case 'firstName':
        setFormErrorOnFirstName(false);
        setHelperTextOnLastName(null);
        break;
      case 'lastName':
        setFormErrorOnLastName(false);
        setHelperTextOnLastName(null);
        break;
      case 'christianName':
        setFormErrorOnChristianName(false);
        setHelperTextOnChristianName(null);
        break;
      case 'position':
        setFormErrorOnPosition(false);
        setHelperTextOnPosition(null);
        break;
      case 'phone':
        setFormErrorOnPhone(false);
        setHelperTextOnPhone(null);
        break;
      case 'educationLevel':
        setFormErrorOnEducationLevel(false);
        setHelperTextOnEducationLevel(null);
        break;
      case 'categoryName':
        setFormErrorOnCategoryName(false);
        setHelperTextOnCategoryName(null);
        break;
      case 'churchName':
        setFormErrorOnChurchName(false);
        setHelperTextOnChurchName(null);
        break;
      case 'roleName':
        setFormErrorOnRoleName(false);
        setHelperTextOnRoleName(null);
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    setFormError(null);
    setFormErrorSeverity('error');

    setFirstNameClicked(false);
    setFormErrorOnFirstName(false);
    setHelperTextOnFirstName(null);

    setLastNameClicked(false);
    setFormErrorOnLastName(false);
    setHelperTextOnLastName(null);

    setChristianNameClicked(false);
    setFormErrorOnChristianName(false);
    setHelperTextOnChristianName(null);

    setPositionClicked(false);
    setFormErrorOnPosition(false);
    setHelperTextOnPosition(null);

    setFormErrorOnBirthDate(false);
    setHelperTextOnBirthDate(null);

    setPhoneClicked(false);
    setFormErrorOnPhone(false);
    setHelperTextOnPhone(null);

    setFormErrorOnMarriageStatus(false);
    setHelperTextOnMarriageStatus(null);

    setEducationLevelClicked(false);
    setFormErrorOnEducationLevel(false);
    setHelperTextOnEducationLevel(null);

    setFormErrorOnGender(false);
    setHelperTextOnGender(null);

    setCategoryNameClicked(false);
    setFormErrorOnCategoryName(false);
    setHelperTextOnCategoryName(null);

    setChurchNameClicked(false);
    setFormErrorOnChurchName(false);
    setHelperTextOnChurchName(null);

    setRoleNameClicked(false);
    setFormErrorOnRoleName(false);
    setHelperTextOnRoleName(null);

    setDisabled(false);
  };

  return (
    <Card
      sx={{ padding: { xs: 1, sm: 2 }, backgroundColor: (theme) => theme.palette.mode === 'light' ? '#FEFEFE' : '#1F1F1F' }}
      component="form"
      autoComplete="off"
      onSubmit={handleSubmitNewUser}
    >
      <Grid container spacing={3}>
        {formError && (
          <Grid item xs={12} sm={12}>
            <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
              {formError}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={formErrorOnFirstName}
            id="firstName"
            name="firstName"
            label="የመጀመሪያ ስም"
            fullWidth
            // variant="standard"
            onChange={handleFormValueChange}
            helperText={formErrorOnFirstName ? helperTextOnFirstName : null}
            onFocus={() => setFirstNameClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={formErrorOnLastName}
            id="lastName"
            name="lastName"
            label="የአባት ስም"
            fullWidth
            // variant="standard"
            onChange={handleFormValueChange}
            helperText={formErrorOnLastName ? helperTextOnLastName : null}
            onFocus={() => setLastNameClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={formErrorOnChristianName}
            id="christianName"
            name="christianName"
            label="የክርስትና ስም"
            fullWidth
            // variant="standard"
            onChange={handleFormValueChange}
            helperText={formErrorOnChristianName ? helperTextOnChristianName : null}
            onFocus={() => setChristianNameClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={formErrorOnPosition}
            id="position"
            name="position"
            label="የቤተ-ክርስትያን የስራ መደብ"
            fullWidth
            // variant="standard"
            onChange={handleFormValueChange}
            helperText={formErrorOnPosition ? helperTextOnPosition : null}
            onFocus={() => setPositionClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={formErrorOnPhone}
            id="phone"
            name="phone"
            label="የስልክ ቁጥር"
            fullWidth
            // variant="standard"
            InputProps={{
              startAdornment: <InputAdornment position="start">+251</InputAdornment>,
            }}
            onChange={handleFormValueChange}
            helperText={formErrorOnPhone ? helperTextOnPhone : null}
            onFocus={() => setPhoneClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <EtDatePicker
            error={formErrorOnBirthDate}
            helperText={formErrorOnBirthDate ? helperTextOnBirthDate : null}
            required
            fullWidth
            name='birthDate'
            // variant="standard"
            label="የትውልድ ዘመን"
            value={birthDate}
            onChange={(selectedDate) => {
              setBirthDate(selectedDate);
              setDisabled(false);
            }}
            minDate={new Date('1700-08-20')}
            maxDate={new Date()}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={['1ኛ ደረጃ', '2ኛ ደረጃ', 'ዲፕሎማ', 'የመጀመሪያ ድግሪ', 'ሁለተኛ ድግሪ', 'ዶክትሬት ድግሪ', 'ፕሮፌሰር', 'ሌላ']}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={formErrorOnEducationLevel}
                name="educationLevel"
                label="የትምህርት ደረጃ"
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnEducationLevel ? helperTextOnEducationLevel : null}
                onFocus={() => setEducationLevelClicked(true)}
                onBlur={handleFormValueBlur}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={
              categories && categories.length >= 1
                ? categories.map((category) => category.name)
                : ['የምድብ ስም አስቀድሞ ከተመዘገቡ ምድቦች ውስጥ አንዱ መሆን አለበት።']
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={formErrorOnCategoryName}
                id="categoryName"
                name="categoryName"
                label="የምድብ ስም"
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnCategoryName ? helperTextOnCategoryName : null}
                onFocus={() => setCategoryNameClicked(true)}
                onBlur={handleFormValueBlur}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <RadioGroup
            required
            error={formErrorOnGender}
            helperText={formErrorOnGender ? helperTextOnGender : null}
            name="gender"
            variant="standard"
          >
            <Box sx={{ display: 'flex', mt: 1 }}>
              <Typography color='text.secondary' sx={{ mr: 1 }}>ጾታ: </Typography>
              <Box
                sx={{ display: 'flex' }}>
                <Radio value="ወንድ" sx={{ mt: -1 }} /> ወንድ
                <Radio value="ሴት" sx={{ mt: -1 }} /> ሴት
              </Box>
            </Box>
          </RadioGroup>
        </Grid>

        <Grid item xs={12} sm={6}>
          <RadioGroup
            required
            error={formErrorOnMarriageStatus}
            helperText={formErrorOnMarriageStatus ? helperTextOnMarriageStatus : null}
            id="marriageStatus"
            name="marriageStatus"
            // variant="standard"
          >
            <Box sx={{ display: 'flex', mt: 1 }}>
              <Typography color='text.secondary' sx={{ mr: 1 }}>የጋብቻ ሁኔታ: </Typography>
              <Box sx={{ display: 'flex' }}>
                <Radio value="ያላገባ" sx={{ mt: -1 }} /> ያላገባ
                <Radio value="ያገባ" sx={{ mt: -1 }} /> ያገባ
                <Radio value="የፈታ" sx={{ mt: -1 }} /> የፈታ
              </Box>
            </Box>
          </RadioGroup>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            disabled={user?.role.name === 'የሃገረስብከት አጥቢያ አስተዳዳሪ'}
            freeSolo
            defaultValue={user && user.church.name}
            options={
              church && church.length >= 1
                ? church.map((oneChurch) => oneChurch.name)
                : ['የአጥቢያ ስም አስቀድሞ ከተመዘገበ አጥቢያ አንዱ መሆን አለበት።']
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={formErrorOnChurchName}
                id="churchName"
                name="churchName"
                label="የአጥቢያ ስም"
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnChurchName ? helperTextOnChurchName : null}
                onFocus={() => setChurchNameClicked(true)}
                onBlur={handleFormValueBlur}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={roles && roles.length >= 1 ? roles : ['የሚና ስም አስቀድሞ ከተመዘገቡት ሚናዎች ውስጥ አንዱ መሆን አለበት።']}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={formErrorOnRoleName}
                id="roleName"
                name="roleName"
                label="የሚና ስም"
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnRoleName ? helperTextOnRoleName : null}
                onFocus={() => setRoleNameClicked(true)}
                onBlur={handleFormValueBlur}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} sx={{ justifyContent: 'right', display: 'flex' }}>
          <Button color="error" type="reset" onClick={handleClearForm} sx={{ minWidth: { xs: 80, sm: 150 } }}>
            <RefreshIcon /> ቅጽ ዳግም አስጀምር
          </Button>
          <Button
            variant="contained"
            type="submit"
            sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
            disabled={disabled}
          >
            {disabled && (
              <>
                <CircularProgress style={{ width: '20px', height: '20px' }} /> ለውጦችን በማስቀመጥ ላይ ...
              </>
            )}
            {!disabled && <>ቀጥል</>}
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
}

UserRegistrationForm.propTypes = {
  setUserToBeUpdated: PropTypes.func.isRequired,
  notification: PropTypes.shape({
    message: PropTypes.string.isRequired,
    severity: PropTypes.string.isRequired,
  }),
  handleNext: PropTypes.func.isRequired,
};
